import { useEffect, useState } from 'react'
import type { Me } from '../lib/me'
import { can, canEverywhere, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { myLanes } from '../lib/support'
import { supabase } from '../lib/supabase'
import { initialsOf } from '../lib/initials'
import { messageOf } from '../lib/errors'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { UsageBody } from '../components/UsageBody'
import { GuideLink } from '../components/guides/GuideLink'
import { loadUsage, PERIODS, type UsageData } from '../lib/usage'

/**
 * How the hub is used - #/admin/usage (301; David, 19 Oct: "active users
 * today, where they are connecting from, mobile or browser, what else?").
 *
 * Whole-company analytics.read: comms_admin unscoped and tenant_admin, the
 * people who already read the video counts (198) and Ask One (126). The nav
 * item shows for any analytics.read holder (the shell has one flag); a scoped
 * holder lands here and is told why, rather than shown an error.
 *
 * One read, usage_dashboard(days), and everything below is drawn from it.
 * COUNTS ONLY - see migration 301's header for what is counted and how small
 * groups are held back. The body is components/UsageBody.tsx so the suite can
 * render it without the shell.
 */
export function AdminUsage({ me }: { me: Me }) {
  const allowed = canEverywhere(me, 'analytics.read')
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<UsageData | null>(null)
  const [error, setError] = useState<string | null>(null)


  useEffect(() => {
    if (!allowed) return
    let live = true
    setError(null)
    loadUsage(days)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(messageOf(e)))
    return () => {
      live = false
    }
  }, [allowed, days])

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Usage"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  if (!allowed) {
    return shell(
      <Card title="Usage">
        <p className="empty">
          The usage page covers the whole company, so it needs the Tenant Administrator role or the Comms Administrator
          role for the whole company.
        </p>
      </Card>,
    )
  }

  return shell(
    <>
      <div className="askone__top">
        <p className="supportinbox__lede">How the hub is used. Every figure is a count; nothing here shows who.</p>
        <GuideLink guide="comms-admin" section="usage" label="How to read these figures" />
        <div className="chips" role="group" aria-label="Period">
          {PERIODS.map((d) => (
            <button
              key={d}
              type="button"
              className={`chip chip--sm${days === d ? ' chip--on' : ''}`}
              aria-pressed={days === d}
              onClick={() => setDays(d)}
            >
              Last {d} days
            </button>
          ))}
        </div>
      </div>
      {error && <p className="roles__error" role="alert">{error}</p>}
      {data ? <UsageBody data={data} /> : !error && <p className="queue__empty">Loading…</p>}
    </>,
  )
}
